/**
 * Full-page interactive calendar (FullCalendar) with sidebar mini-month,
 * create/edit dialog, and event detail popover. Uses multi-tenant events API.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import api, { enhancedApi, getEvents, createEvent, updateEvent, deleteEvent } from '../api';
import ConfirmDeleteModal from './ui/ConfirmDeleteModal';
import { tokens } from '../theme';
import './EventsCalendar.css';

dayjs.extend(relativeTime);

const CAL_HEIGHT = `calc(100dvh - ${tokens.eventsCalendarVerticalOffsetPx}px)`;
/** Mini month needs ~7 day columns; 240px clips F/Sat — use full MUI grid width */
const SIDEBAR_W = 320;

const UI_EVENT_TYPES = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'call', label: 'Call' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'block_time', label: 'Block Time' },
  { value: 'other', label: 'Other' },
];

const REMINDER_OPTIONS = [
  { value: 'none', label: 'None', minutes: null },
  { value: '15', label: '15 min', minutes: 15 },
  { value: '30', label: '30 min', minutes: 30 },
  { value: '60', label: '1 hour', minutes: 60 },
  { value: '1440', label: '1 day', minutes: 1440 },
];

const VIEW_TOGGLE = [
  { id: 'month', label: 'Month', fc: 'dayGridMonth' },
  { id: 'week', label: 'Week', fc: 'timeGridWeek' },
  { id: 'day', label: 'Day', fc: 'timeGridDay' },
  { id: 'schedule', label: 'Schedule', fc: 'listWeek' },
];

function tagList(ev) {
  const t = ev?.tags;
  return Array.isArray(t) ? t : [];
}

function inferUiType(ev) {
  const tags = tagList(ev);
  if (tags.includes('block_time')) return 'block_time';
  if (tags.includes('call')) return 'call';
  if (ev.event_type === 'deadline') return 'deadline';
  if (ev.event_type === 'reminder') return 'follow_up';
  if (ev.event_type === 'appointment') return 'meeting';
  return 'other';
}

function mapUiToApiEventType(ui) {
  const m = {
    meeting: 'appointment',
    call: 'appointment',
    deadline: 'deadline',
    follow_up: 'reminder',
    block_time: 'task',
    other: 'task',
  };
  return m[ui] || 'task';
}

function buildTags(uiType, existingTags) {
  const prev = Array.isArray(existingTags) ? existingTags : [];
  const cleaned = prev.filter((x) => !['block_time', 'call'].includes(x));
  const next = new Set(cleaned);
  if (uiType === 'block_time') next.add('block_time');
  if (uiType === 'call') next.add('call');
  return [...next];
}

function parseMetaFromDescription(description) {
  let rest = String(description || '');
  let location = '';
  let attendeesLine = '';
  const locRe = /^\s*Location:\s*([^\n]+)(?:\n|$)/i;
  const attRe = /^\s*Attendees:\s*([^\n]+)(?:\n|$)/i;
  let m = rest.match(locRe);
  if (m) {
    location = m[1].trim();
    rest = rest.slice(m[0].length).trim();
  }
  m = rest.match(attRe);
  if (m) {
    attendeesLine = m[1].trim();
    rest = rest.slice(m[0].length).trim();
  }
  const attendeeNames = attendeesLine
    ? attendeesLine.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  return { location, attendeeNames, userDescription: rest };
}

function composeDescription({ location, attendeeNames, userDescription }) {
  const parts = [];
  if (location?.trim()) parts.push(`Location: ${location.trim()}`);
  if (attendeeNames?.length) parts.push(`Attendees: ${attendeeNames.join(', ')}`);
  if (userDescription?.trim()) parts.push(userDescription.trim());
  return parts.join('\n\n');
}

function eventColorsFromTheme(theme) {
  return {
    meeting: { bg: '#1976d2', border: '#1565c0' },
    call: { bg: '#2e7d32', border: '#1b5e20' },
    deadline: { bg: theme.palette.error.main, border: theme.palette.error.dark },
    follow_up: { bg: theme.palette.warning.main, border: theme.palette.warning.dark },
    block_time: { bg: '#757575', border: '#616161' },
    other: { bg: theme.palette.secondary.main, border: theme.palette.secondary.dark },
  };
}

function mapRawToFullCalendar(raw, colors) {
  const startSrc = raw.start_datetime || raw.due_date;
  if (!startSrc) return null;

  const uiType = inferUiType(raw);
  const c = colors[uiType];
  const start = dayjs(startSrc);

  if (raw.all_day) {
    const d0 = start.format('YYYY-MM-DD');
    const endExclusive = raw.end_datetime
      ? dayjs(raw.end_datetime).add(1, 'day').format('YYYY-MM-DD')
      : dayjs(startSrc).add(1, 'day').format('YYYY-MM-DD');
    return {
      id: String(raw.id),
      title: raw.title,
      start: d0,
      end: endExclusive,
      allDay: true,
      extendedProps: { raw, uiType, block: uiType === 'block_time' },
      backgroundColor: c.bg,
      borderColor: c.border,
      textColor: '#fff',
    };
  }

  const startIso = start.toDate();
  let endDate;
  if (raw.end_datetime) {
    endDate = dayjs(raw.end_datetime).toDate();
  } else {
    endDate = dayjs(startSrc).add(1, 'hour').toDate();
  }
  return {
    id: String(raw.id),
    title: raw.title,
    start: startIso,
    end: endDate,
    allDay: false,
    extendedProps: { raw, uiType, block: uiType === 'block_time' },
    backgroundColor: c.bg,
    borderColor: c.border,
    textColor: '#fff',
  };
}

function pickStaffUsers(users) {
  if (!Array.isArray(users)) return [];
  return users;
}

/** Timed events: start must not be before this instant. */
function isStartInPast(start) {
  return dayjs(start).isBefore(dayjs());
}

/** All-day events: calendar day must not be before today (local). */
function isAllDayDateInPast(d) {
  return dayjs(d).startOf('day').isBefore(dayjs().startOf('day'));
}

function staffLabel(u) {
  const n = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return n || u.username || `User ${u.id}`;
}

function EventDay(props) {
  const { day, outsideCurrentMonth, dayHasEvent, ...pickersProps } = props;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto' }}>
      <PickersDay
        {...pickersProps}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{ position: 'relative', width: 36, height: 36 }}
      />
      {dayHasEvent && !outsideCurrentMonth ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Box sx={{ flex: 1, minHeight: CAL_HEIGHT, p: 2 }}>
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={36} />
        <Skeleton variant="rounded" height={CAL_HEIGHT} sx={{ flex: 1 }} />
      </Stack>
    </Box>
  );
}

export default function EventsCalendar() {
  const theme = useTheme();
  const colors = useMemo(() => eventColorsFromTheme(theme), [theme]);
  const calRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [toolbarTitle, setToolbarTitle] = useState('');

  const [viewMode, setViewMode] = useState('week');
  const [miniMonth, setMiniMonth] = useState(() => dayjs());

  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [popover, setPopover] = useState({ anchorEl: null, eventRecord: null });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pastCreateSnackOpen, setPastCreateSnackOpen] = useState(false);

  // Google Calendar connection state
  const [calendarConnected, setCalendarConnected] = useState(null);
  const [calendarBannerDismissed, setCalendarBannerDismissed] = useState(
    () => localStorage.getItem('calendar_banner_dismissed') === 'true'
  );
  const [syncing, setSyncing] = useState(false);

  const defaultForm = useMemo(
    () => ({
      title: '',
      uiType: 'meeting',
      client: null,
      start: dayjs().add(1, 'hour').startOf('hour'),
      end: dayjs().add(2, 'hour').startOf('hour'),
      allDay: false,
      location: '',
      userDescription: '',
      attendees: [],
      reminder: 'none',
      markAsBusy: true,
    }),
    []
  );
  const [form, setForm] = useState(defaultForm);
  const prevUiTypeRef = useRef('meeting');

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getEvents();
      const list = Array.isArray(res.data) ? res.data : [];
      setRawEvents(list);
    } catch {
      setLoadError(true);
      setRawEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Check Google Calendar connection status on mount
  useEffect(() => {
    const checkCalendarStatus = async () => {
      try {
        const res = await enhancedApi.getCalendarStatus();
        setCalendarConnected(res?.data?.connected ?? false);
      } catch {
        setCalendarConnected(false);
      }
    };
    checkCalendarStatus();
  }, []);

  const handleConnectCalendar = async () => {
    try {
      const res = await enhancedApi.initiateCalendarAuth();
      if (res?.data?.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch {
      // silently fail — user can try again
    }
  };

  const handleSyncCalendar = async () => {
    setSyncing(true);
    try {
      await api.post('/api/calendar/sync/');
      await loadEvents();
    } catch {
      // silently fail
    } finally {
      setSyncing(false);
    }
  };

  const handleDismissBanner = () => {
    setCalendarBannerDismissed(true);
    localStorage.setItem('calendar_banner_dismissed', 'true');
  };

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setDialogMode('create');
      setEditingId(null);
      setForm({
        ...defaultForm,
        start: dayjs().add(1, 'hour').startOf('hour'),
        end: dayjs().add(2, 'hour').startOf('hour'),
      });
      setDialogOpen(true);
    }
  }, [searchParams, defaultForm]);

  useEffect(() => {
    const run = async () => {
      try {
        const [cRes, uRes] = await Promise.all([
          enhancedApi.getClients(),
          api.get('/api/multi-tenant/users/'),
        ]);
        const cData = cRes?.data;
        setClients(Array.isArray(cData) ? cData : cData?.results || cData?.data || []);
        const uData = uRes?.data;
        setUsers(Array.isArray(uData) ? uData : uData?.users || []);
      } catch {
        setClients([]);
        setUsers([]);
      }
    };
    run();
  }, []);

  const fcEvents = useMemo(
    () => rawEvents.map((r) => mapRawToFullCalendar(r, colors)).filter(Boolean),
    [rawEvents, colors]
  );

  const daysWithEvents = useMemo(() => {
    const set = new Set();
    rawEvents.forEach((ev) => {
      const s = ev.start_datetime || ev.due_date;
      if (s) set.add(dayjs(s).format('YYYY-MM-DD'));
    });
    return set;
  }, [rawEvents]);

  const upcomingFive = useMemo(() => {
    const now = dayjs();
    return [...rawEvents]
      .filter((ev) => {
        const t = ev.start_datetime || ev.due_date || ev.end_datetime;
        if (!t) return false;
        return dayjs(t).isAfter(now) && ev.status !== 'cancelled';
      })
      .sort((a, b) => {
        const ta = dayjs(a.start_datetime || a.due_date).valueOf();
        const tb = dayjs(b.start_datetime || b.due_date).valueOf();
        return ta - tb;
      })
      .slice(0, 5);
  }, [rawEvents]);

  const openCreateFromSlot = (start, end) => {
    const s = dayjs(start);
    if (isStartInPast(s)) {
      setPastCreateSnackOpen(true);
      return;
    }
    let e = end ? dayjs(end) : s.add(1, 'hour');
    if (!e.isAfter(s)) e = s.add(1, 'hour');
    setDialogMode('create');
    setEditingId(null);
    setForm({
      ...defaultForm,
      title: '',
      start: s,
      end: e,
      allDay: false,
    });
    setDialogOpen(true);
  };

  const openCreateBlank = () => {
    const s = dayjs().add(1, 'hour').startOf('hour');
    setDialogMode('create');
    setEditingId(null);
    setForm({
      ...defaultForm,
      start: s,
      end: s.add(1, 'hour'),
    });
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    if (!record) return;
    const meta = parseMetaFromDescription(record.description);
    const ui = inferUiType(record);
    const startBase = record.start_datetime || record.due_date;
    const staff = pickStaffUsers(users);
    const matched = [];
    if (record.assigned_to) {
      const u = staff.find((x) => x.id === record.assigned_to);
      if (u) matched.push(u);
    }
    meta.attendeeNames.forEach((name) => {
      if (matched.length >= 10) return;
      const found = staff.find((u) => staffLabel(u).toLowerCase() === name.toLowerCase());
      if (found && !matched.find((m) => m.id === found.id)) matched.push(found);
    });

    const rm =
      Array.isArray(record.reminder_minutes_before) && record.reminder_minutes_before.length
        ? record.reminder_minutes_before[0]
        : undefined;
    const reminderVal =
      rm != null && REMINDER_OPTIONS.some((o) => o.minutes === rm) ? String(rm) : 'none';

    setDialogMode('edit');
    setEditingId(record.id);
    setForm({
      title: record.title || '',
      uiType: ui,
      client: record.client ? clients.find((c) => c.id === record.client) || null : null,
      start: startBase ? dayjs(startBase) : dayjs(),
      end: record.end_datetime ? dayjs(record.end_datetime) : dayjs(startBase || undefined).add(1, 'hour'),
      allDay: !!record.all_day,
      location: meta.location,
      userDescription: meta.userDescription,
      attendees: matched,
      reminder: reminderVal,
      markAsBusy: record.sync_to_calendar !== false,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSaving(false);
    setForm(defaultForm);
    setEditingId(null);
  };

  const buildPayloadFromForm = () => {
    const uiType = form.uiType;
    const apiType = mapUiToApiEventType(uiType);
    const reminderOpt = REMINDER_OPTIONS.find((r) => r.value === form.reminder);
    const reminderMinutes =
      reminderOpt && reminderOpt.minutes != null ? [reminderOpt.minutes] : [];

    const attendeeNames = form.attendees?.map((u) => staffLabel(u)).filter(Boolean) || [];
    const firstAssign = form.attendees?.[0]?.id ?? null;

    let title = (form.title || '').trim();
    if (uiType === 'block_time') {
      title = title || 'Busy';
    }

    const description = composeDescription({
      location: form.location,
      attendeeNames,
      userDescription: form.userDescription,
    });

    const base = {
      title,
      description,
      event_type: apiType,
      status: 'scheduled',
      client: form.client?.id ?? null,
      all_day: form.allDay,
      assigned_to: firstAssign,
      reminder_minutes_before: reminderMinutes,
      sync_to_calendar: uiType === 'block_time' ? !!form.markAsBusy : true,
    };

    if (form.allDay) {
      base.start_datetime = form.start.startOf('day').toISOString();
      base.end_datetime = form.end ? form.end.startOf('day').toISOString() : form.start.add(1, 'day').startOf('day').toISOString();
      base.due_date = null;
    } else {
      base.start_datetime = form.start.toISOString();
      base.end_datetime = form.end ? form.end.toISOString() : form.start.add(1, 'hour').toISOString();
      base.due_date = null;
    }
    return base;
  };

  const handleSave = async () => {
    const payload = buildPayloadFromForm();
    if (!payload.title || payload.title.length < 3) return;
    if (dialogMode === 'create') {
      if (form.allDay) {
        if (isAllDayDateInPast(form.start)) {
          setPastCreateSnackOpen(true);
          return;
        }
      } else if (form.start.isBefore(dayjs())) {
        setPastCreateSnackOpen(true);
        return;
      }
    }
    setSaving(true);
    try {
      if (dialogMode === 'edit' && editingId) {
        const prev = rawEvents.find((e) => e.id === editingId);
        const mergedTags = buildTags(form.uiType, prev?.tags);
        await updateEvent(editingId, {
          ...payload,
          tags: mergedTags,
        });
      } else {
        await createEvent({
          ...payload,
          tags: buildTags(form.uiType, []),
        });
      }
      closeDialog();
      setPopover({ anchorEl: null, eventRecord: null });
      loadEvents();
    } catch {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!popover.eventRecord) return;
    setDeleting(true);
    try {
      await deleteEvent(popover.eventRecord.id);
      setDeleteOpen(false);
      setPopover({ anchorEl: null, eventRecord: null });
      loadEvents();
    } finally {
      setDeleting(false);
    }
  };

  const onEventDropOrResize = async (info) => {
    const id = Number(info.event.id);
    try {
      const start = info.event.start;
      if (!info.event.allDay && dayjs(start).isBefore(dayjs())) {
        info.revert();
        setPastCreateSnackOpen(true);
        return;
      }
      if (info.event.allDay && isAllDayDateInPast(start)) {
        info.revert();
        setPastCreateSnackOpen(true);
        return;
      }
      const end = info.event.end || dayjs(start).add(1, 'hour').toDate();
      await updateEvent(id, {
        start_datetime: start.toISOString(),
        end_datetime: info.event.allDay ? dayjs(end).endOf('day').toISOString() : end.toISOString(),
        all_day: !!info.event.allDay,
      });
      loadEvents();
    } catch {
      info.revert();
    }
  };

  const calendarApi = () => calRef.current?.getApi();

  const onDatesSet = (arg) => {
    setToolbarTitle(arg.view.title);
    setMiniMonth(dayjs(arg.view.currentStart));
  };

  const handleViewChange = (_, v) => {
    if (!v) return;
    setViewMode(v);
    const row = VIEW_TOGGLE.find((x) => x.id === v);
    if (row) calendarApi()?.changeView(row.fc);
  };

  const eventClickHandler = (info) => {
    setPopover({ anchorEl: info.el, eventRecord: info.event.extendedProps.raw });
  };

  useEffect(() => {
    if (form.uiType === 'block_time' && prevUiTypeRef.current !== 'block_time') {
      setForm((f) => ({
        ...f,
        title: f.title?.trim().length >= 3 ? f.title : 'Busy',
      }));
    }
    prevUiTypeRef.current = form.uiType;
  }, [form.uiType]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        className="events-calendar-root"
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: CAL_HEIGHT }}
      >
        {loadError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadEvents}>
                Retry
              </Button>
            }
            sx={{ borderRadius: 0 }}
          >
            Could not load events
          </Alert>
        )}

        {/* Google Calendar connection banner */}
        {calendarConnected === false && !calendarBannerDismissed && (
          <Alert
            severity="info"
            sx={{ borderRadius: 0 }}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button color="inherit" size="small" variant="outlined" onClick={handleConnectCalendar}>
                  Connect Google Calendar
                </Button>
                <IconButton size="small" color="inherit" onClick={handleDismissBanner}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            Connect Google Calendar to sync your events
          </Alert>
        )}

        {/* Sync Now button when connected */}
        {calendarConnected === true && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
            <Button
              size="small"
              startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={handleSyncCalendar}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </Box>
        )}

        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Calendar
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
              <IconButton size="small" onClick={() => calendarApi()?.prev()} aria-label="Previous" sx={{ border: 1, borderColor: 'divider' }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography sx={{ fontWeight: 700, minWidth: 140, textAlign: 'center' }}>{toolbarTitle}</Typography>
              <IconButton size="small" onClick={() => calendarApi()?.next()} aria-label="Next" sx={{ border: 1, borderColor: 'divider' }}>
                <ChevronRightIcon />
              </IconButton>
              <Button size="small" variant="outlined" onClick={() => calendarApi()?.today()}>
                Today
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={handleViewChange}
                aria-label="Calendar view"
              >
                {VIEW_TOGGLE.map((v) => (
                  <ToggleButton key={v.id} value={v.id}>
                    {v.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateBlank}>
                Create Event
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <Box
            component="aside"
            className="events-calendar-mini-wrap"
            sx={{
              width: SIDEBAR_W,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              py: 1,
              px: 0.5,
              overflowX: 'auto',
              overflowY: 'auto',
              boxSizing: 'border-box',
            }}
          >
            <DateCalendar
              className="events-calendar-mini"
              value={miniMonth}
              onChange={(d) => {
                if (!d) return;
                setMiniMonth(d);
                calendarApi()?.gotoDate(d.toDate());
              }}
              sx={{
                width: '100%',
                maxWidth: '100%',
                '& .MuiPickersCalendarHeader-root': { px: 0.5, pr: 0 },
                '& .MuiDayCalendar-header': { justifyContent: 'space-around' },
                '& .MuiDayCalendar-weekContainer': { justifyContent: 'space-around', margin: 0 },
              }}
              slots={{
                day: (dayProps) => (
                  <EventDay
                    {...dayProps}
                    dayHasEvent={daysWithEvents.has(dayjs(dayProps.day).format('YYYY-MM-DD'))}
                  />
                ),
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
              Upcoming
            </Typography>
            <Stack spacing={0.5}>
              {upcomingFive.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No upcoming events
                </Typography>
              ) : (
                upcomingFive.map((ev) => {
                  const ui = inferUiType(ev);
                  const c = colors[ui];
                  const t = ev.start_datetime || ev.due_date;
                  return (
                    <Box
                      key={ev.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (t) {
                          calendarApi()?.gotoDate(new Date(t));
                          const row = VIEW_TOGGLE.find((x) => x.id === viewMode);
                          if (row?.fc === 'dayGridMonth') calendarApi()?.changeView('timeGridDay');
                        }
                        setPopover({ anchorEl: null, eventRecord: ev });
                        setTimeout(() => {
                          /* let view settle */
                        }, 0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (t) calendarApi()?.gotoDate(new Date(t));
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 0.75,
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.bg, mt: 0.6, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {ev.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t ? dayjs(t).fromNow() : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <LoadingSkeleton />
            ) : (
              <Box sx={{ flex: 1, minHeight: CAL_HEIGHT }}>
                <FullCalendar
                  ref={calRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                  initialView="timeGridWeek"
                  headerToolbar={false}
                  height={CAL_HEIGHT}
                  editable
                  selectable
                  selectAllow={(selectInfo) => !dayjs(selectInfo.start).isBefore(dayjs())}
                  selectMirror
                  nowIndicator
                  dayMaxEvents
                  businessHours={{ daysOfWeek: [1, 2, 3, 4, 5], startTime: '08:00', endTime: '18:00' }}
                  slotMinTime="06:00:00"
                  slotMaxTime="22:00:00"
                  events={fcEvents}
                  datesSet={onDatesSet}
                  eventClick={eventClickHandler}
                  select={(info) => {
                    if (isStartInPast(info.start)) {
                      setPastCreateSnackOpen(true);
                      calendarApi()?.unselect();
                      return;
                    }
                    openCreateFromSlot(info.start, info.end);
                    calendarApi()?.unselect();
                  }}
                  dateClick={(info) => {
                    if (info.view.type !== 'dayGridMonth') return;
                    const dayStart = dayjs(info.date).startOf('day');
                    if (dayStart.isBefore(dayjs().startOf('day'))) {
                      setPastCreateSnackOpen(true);
                      return;
                    }
                    let startD;
                    if (dayStart.isSame(dayjs(), 'day')) {
                      startD = dayjs().add(1, 'hour').startOf('hour');
                      if (startD.isBefore(dayjs())) startD = dayjs().add(5, 'minute');
                    } else {
                      startD = dayStart.hour(9).minute(0).second(0).millisecond(0);
                    }
                    if (startD.isBefore(dayjs())) {
                      setPastCreateSnackOpen(true);
                      return;
                    }
                    const endD = startD.add(1, 'hour');
                    openCreateFromSlot(startD.toDate(), endD.toDate());
                  }}
                  eventDrop={onEventDropOrResize}
                  eventResize={onEventDropOrResize}
                  eventContent={(arg) => (
                    <Box
                      className="fc-event-main-frame"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        overflow: 'hidden',
                        px: 0.25,
                      }}
                    >
                      {arg.event.extendedProps.block ? (
                        <LockIcon sx={{ fontSize: 14, flexShrink: 0, opacity: 0.9 }} />
                      ) : null}
                      <span className="fc-event-title">{arg.event.title}</span>
                    </Box>
                  )}
                  noEventsContent={
                    <Typography variant="body2" color="text.secondary">
                      No events — click any slot to create
                    </Typography>
                  }
                />
              </Box>
            )}
          </Box>
        </Box>

        <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth scroll="paper">
          <DialogTitle>{dialogMode === 'edit' ? 'Edit event' : 'Create event'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Event title"
                required
                autoFocus
                fullWidth
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                error={form.title.length > 0 && form.title.trim().length < 3}
                helperText={form.title.length > 0 && form.title.trim().length < 3 ? 'At least 3 characters' : ' '}
              />

              <FormControl fullWidth size="small">
                <InputLabel id="ev-type-label">Event Type</InputLabel>
                <Select
                  labelId="ev-type-label"
                  label="Event Type"
                  value={form.uiType}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      uiType: e.target.value,
                      title: e.target.value === 'block_time' ? 'Busy' : f.title,
                    }))
                  }
                >
                  {UI_EVENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {form.uiType === 'block_time' ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.markAsBusy}
                      onChange={(e) => setForm((f) => ({ ...f, markAsBusy: e.target.checked }))}
                    />
                  }
                  label="Mark as Busy (sync to external calendar when connected)"
                />
              ) : null}

              <Autocomplete
                options={clients}
                value={form.client}
                onChange={(_, v) => setForm((f) => ({ ...f, client: v }))}
                getOptionLabel={(c) =>
                  c?.display_name ||
                  c?.primary_contact?.display_name ||
                  `${c?.first_name || ''} ${c?.last_name || ''}`.trim() ||
                  (c?.id ? `Client #${c.id}` : '')
                }
                renderInput={(params) => <TextField {...params} label="Client (optional)" size="small" />}
              />

              <FormControlLabel
                control={<Switch checked={form.allDay} onChange={(e) => setForm((f) => ({ ...f, allDay: e.target.checked }))} />}
                label="All day"
              />

              <DateTimePicker
                label="Start *"
                value={form.start}
                onChange={(v) => v && setForm((f) => ({ ...f, start: v }))}
                minDate={dialogMode === 'create' ? dayjs().startOf('day') : undefined}
                minDateTime={dialogMode === 'create' && !form.allDay ? dayjs() : undefined}
                shouldDisableDate={(d) =>
                  dialogMode === 'create' ? dayjs(d).startOf('day').isBefore(dayjs().startOf('day')) : false
                }
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DateTimePicker
                label="End"
                value={form.end}
                onChange={(v) => v && setForm((f) => ({ ...f, end: v }))}
                minDate={dialogMode === 'create' ? dayjs().startOf('day') : undefined}
                minDateTime={
                  dialogMode === 'create' && !form.allDay && form.start
                    ? form.start.isAfter(dayjs())
                      ? form.start
                      : dayjs()
                    : undefined
                }
                shouldDisableDate={(d) =>
                  dialogMode === 'create' ? dayjs(d).startOf('day').isBefore(dayjs().startOf('day')) : false
                }
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />

              <TextField
                label="Location"
                fullWidth
                size="small"
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={2}
                value={form.userDescription}
                onChange={(e) => setForm((f) => ({ ...f, userDescription: e.target.value }))}
              />

              <Autocomplete
                multiple
                options={pickStaffUsers(users)}
                value={form.attendees}
                onChange={(_, v) => setForm((f) => ({ ...f, attendees: v }))}
                getOptionLabel={(u) => staffLabel(u)}
                renderInput={(params) => <TextField {...params} label="Attendees" size="small" placeholder="Staff" />}
              />

              <FormControl fullWidth size="small">
                <InputLabel id="rem-label">Reminder</InputLabel>
                <Select
                  labelId="rem-label"
                  label="Reminder"
                  value={form.reminder}
                  onChange={(e) => setForm((f) => ({ ...f, reminder: e.target.value }))}
                >
                  {REMINDER_OPTIONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || form.title.trim().length < 3}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {dialogMode === 'edit' ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <EventDetailPopover
          open={Boolean(popover.anchorEl)}
          anchorEl={popover.anchorEl}
          onClose={() => setPopover({ anchorEl: null, eventRecord: null })}
          record={popover.eventRecord}
          colors={colors}
          onEdit={() => {
            const r = popover.eventRecord;
            setPopover({ anchorEl: null, eventRecord: null });
            openEdit(r);
          }}
          onDelete={() => setDeleteOpen(true)}
        />

        <ConfirmDeleteModal
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={handleDelete}
          itemName={popover.eventRecord?.title || 'Event'}
          itemType="event"
          loading={deleting}
        />

        <Snackbar
          open={pastCreateSnackOpen}
          autoHideDuration={4500}
          onClose={() => setPastCreateSnackOpen(false)}
          message="You can only schedule events from the current time onward (not on past days or times)."
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </LocalizationProvider>
  );
}

function EventDetailPopover({ open, anchorEl, onClose, record, colors, onEdit, onDelete }) {
  if (!record) return null;
  const ui = inferUiType(record);
  const c = colors[ui];
  const meta = parseMetaFromDescription(record.description);
  const start = record.start_datetime || record.due_date;
  const end = record.end_datetime;
  const label = UI_EVENT_TYPES.find((x) => x.value === ui)?.label || ui;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: { sx: { p: 2, maxWidth: 360, borderRadius: 2 } },
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {record.title}
          </Typography>
          <Chip size="small" label={label} sx={{ bgcolor: c.bg, color: '#fff', border: 'none' }} />
        </Box>

        {record.client && record.client_name ? (
          <Typography variant="body2">
            Client:{' '}
            <Button
              component={RouterLink}
              to={`/client/${record.client}`}
              size="small"
              sx={{ verticalAlign: 'baseline', p: 0, minWidth: 0 }}
            >
              {record.client_name}
            </Button>
          </Typography>
        ) : null}

        <Typography variant="body2" color="text.secondary">
          {start
            ? `${dayjs(start).format(record.all_day ? 'MMM D, YYYY' : 'MMM D, YYYY h:mm A')}${
                end ? ` – ${dayjs(end).format(record.all_day ? 'MMM D, YYYY' : 'MMM D, YYYY h:mm A')}` : ''
              }`
            : '—'}
        </Typography>

        {meta.location ? (
          <Typography variant="body2">
            <strong>Location:</strong> {meta.location}
          </Typography>
        ) : null}

        {meta.userDescription ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {meta.userDescription}
          </Typography>
        ) : null}

        {(record.assigned_to_name || meta.attendeeNames.length > 0) && (
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {record.assigned_to_name ? (
              <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{record.assigned_to_name.slice(0, 2)}</Avatar>
            ) : null}
            {meta.attendeeNames.map((n) => (
              <Avatar key={n} sx={{ width: 28, height: 28, fontSize: 12 }}>
                {n.slice(0, 2)}
              </Avatar>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
            Edit
          </Button>
          <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete}>
            Delete
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
